import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LatestMatchesArmMapping } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/types'
import { generateLatestMatchesExperimentPayload } from '@lyrahealth-inc/shared-app-logic/src/features/experiments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'

import {
  NavigateToLatestProviderMatchesProps,
  useNavigateToLatestProviderMatches,
} from './useNavigateToLatestProviderMatches'
import { postExperimentEntrypoint } from '../../data/appGlobals/appGlobalsActions'
import { getDeviceUUID } from '../../data/appGlobals/appGlobalsSelectors'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getId, getIsUserLoggedIn } from '../../data/user/userSelectors'
import { setLoadingUserFavoriteProviders } from '../../features/onboard/data/onboardDataActions'
import { getLatestUserFavoriteProviders } from '../../features/onboard/data/onboardSelectors'

export enum LatestMatchesEntrypoint {
  FORKED_TRIAGE = 'forked triage',
  FAVORITED_PROVIDERS = 'favorited providers page',
  REENGAGEMENT_EMAIL = 'reengagement email',
}

/**
 * Inits latest matches (`fetchLatestUserFavoriteProviders` and calls experiment endpoint with the viewed variant) and returns relevant props such as hasLatestMatches
 * Can be invoked from the forked triage page or the favorited providers page. The forked triage page has its own flag to determine if the feature is enabled
 *
 * If invoked from the favorited providers page, the forked triage flag is ignored
 */
export const useInitLatestMatchesAndGetProps = ({
  navigateToLatestProviderMatchesProps,
  fetchLatestUserFavoriteProviders,
  entrypoint,
  searchesLoaded = false,
}: {
  navigateToLatestProviderMatchesProps: NavigateToLatestProviderMatchesProps
  fetchLatestUserFavoriteProviders: ({ latestSearchId }: { latestSearchId: string }) => any
  entrypoint: LatestMatchesEntrypoint
  searchesLoaded?: boolean
}) => {
  const dispatch = useAppDispatch()
  const latestUserProviders = useSelector(getLatestUserFavoriteProviders)
  const { isLatestMatchesEnabled: isLatestMatchesOnForkedTriageEnabled } = useFlags()
  const [fetchedUserFavoriteProviders, setFetchedUserFavoriteProviders] = useState(false)
  const deviceUUID = useSelector(getDeviceUUID)
  const userId = useSelector(getId)
  const isLoggedIn = useSelector(getIsUserLoggedIn)

  const navigateToLatestProviderMatches = useNavigateToLatestProviderMatches({
    ...navigateToLatestProviderMatchesProps,
  })
  const { inProgressSearch } = navigateToLatestProviderMatchesProps
  const latestSearchId = inProgressSearch?.id

  const ENTRYPOINTS_WITH_ACCESS_TO_LATEST_MATCHES = [
    LatestMatchesEntrypoint.FAVORITED_PROVIDERS,
    LatestMatchesEntrypoint.REENGAGEMENT_EMAIL,
  ]

  // Set loading to false when we've confirmed there are no latest matches
  useEffect(() => {
    if (isLoggedIn && fetchedUserFavoriteProviders && searchesLoaded && latestUserProviders?.length === undefined) {
      dispatch(setLoadingUserFavoriteProviders(false))
    }
  }, [isLoggedIn, fetchedUserFavoriteProviders, latestUserProviders?.length, searchesLoaded, dispatch])

  useFetcher(
    [[fetchLatestUserFavoriteProviders, { latestSearchId }, latestSearchId && isLoggedIn]],
    [latestSearchId, fetchLatestUserFavoriteProviders],
    {
      onFinally: () => {
        setFetchedUserFavoriteProviders(true)
      },
    },
  )

  /* Experiment tracking for latest matches function on the forked triage container, not associated with the favorited providers page */
  useEffect(() => {
    if (entrypoint !== LatestMatchesEntrypoint.FORKED_TRIAGE) {
      return
    }
    if (isLoggedIn && fetchedUserFavoriteProviders) {
      const latestMatchesExperimentPayload = generateLatestMatchesExperimentPayload({
        arm: isLatestMatchesOnForkedTriageEnabled
          ? LatestMatchesArmMapping.CAN_SEE
          : LatestMatchesArmMapping.CANNOT_SEE,
        client_lyra_id: userId,
        device_uuid: deviceUUID,
      })
      postExperimentEntrypoint(latestMatchesExperimentPayload)
    }
  }, [
    isLatestMatchesOnForkedTriageEnabled,
    latestUserProviders,
    userId,
    fetchedUserFavoriteProviders,
    deviceUUID,
    isLoggedIn,
    entrypoint,
  ])

  return {
    onLatestMatches: navigateToLatestProviderMatches,
    hasLatestMatches: useMemo(
      () =>
        latestUserProviders?.length > 0 &&
        (ENTRYPOINTS_WITH_ACCESS_TO_LATEST_MATCHES.includes(entrypoint) ||
          (entrypoint === LatestMatchesEntrypoint.FORKED_TRIAGE && isLatestMatchesOnForkedTriageEnabled)),
      [entrypoint, isLatestMatchesOnForkedTriageEnabled, latestUserProviders?.length],
    ),
    fetchedUserFavoriteProviders,
  }
}
